// frontend/src/components/Navbar.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import { Menu as MenuIcon, X as CloseIcon, Fingerprint, LayoutGrid, ChartNoAxesCombined, User, Settings, ShieldEllipsis, PersonStanding, Globe, ChevronDown, Aperture, Repeat, BookOpen, PanelsTopLeft, Smartphone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useGuide } from "../contexts/GuideContext";
import SimpleAccessibilitySettings from "./SimpleAccessibilitySettings";
import InstallInstructionsDialog from "./InstallInstructionsDialog";
import useUpgradeOffer from "../hooks/useUpgradeOffer";
import {
  OFFER_KIND,
  navbarChipView,
  offerKindEventParams,
  resolveOfferView,
} from "../utils/friendCodeOffer";
import { UpgradeDrawerRow, UpgradePill } from "./upsell/UpgradePill";
import { useLastDayBandOnScreen } from "./cardOnFile/lastDayBandPresence";
import { resolveTrialOfferStrip } from "./cardOnFile/trialOfferStripView";
import { resolveCardOnFileView } from "./cardOnFile/cardOnFileView";
import { variantForSurface } from "./upsell/upsellStyles";
import analytics from "../services/analytics";
import { colors } from "../theme/tokens";
import { setActiveRole } from "../utils/activeRole";
import { useIsStandalonePwa } from "../utils/pwaDisplayMode";
import { useInstallAction } from "../utils/pwaInstall";
// Recommender-variant earnings item: its own import line, so the shared icon
// import above stays untouched.
import { Wallet } from "lucide-react";
// The phone menu's "ארכיון" row: its icon, the archive's own copy table (the
// word is the archive feature's, not the navbar's) and the SHARED eligibility
// rule + request shape the dashboard reads too.
import { Archive } from "lucide-react";
import { archiveText } from "./leads/archive/archiveCopy";
import { accountMayUseArchive, openArchiveNavigation } from "./leads/archive/archiveAccess";
import Logo from "./brand/Logo";
import {
  ADMIN_GROUP_HEADING_ID,
  ADMIN_TOOL_PATHS,
  NAV_ACCOUNT_NAME_MAX_PX,
  NAV_ACTIVE_BG,
  NAV_HAIRLINE,
  NAV_INK,
  NAV_LOCKUP_HEIGHT,
  NAV_MENU_BREAKPOINT_PX,
  NAV_MENU_BREAKPOINT_WIDE_PX,
  NAV_ROW_FLOOR_PX,
  NAV_SLATE,
  NAV_WIDE_ROW_ITEMS,
  SIGNATURE_ICON_COLORS,
} from "./navbar/navTokens";
import useRowFit, { canMeasureRow } from "./navbar/useRowFit";
import { VISUALLY_HIDDEN_SX } from "../styles/visuallyHidden";
// The shared pill vocabulary first, then this bar's own layout on top.
import "./navbar/pillNav.css";
import "./navbar/appNav.css";

/** The phone menu's id - the burger's `aria-controls` target while it is open. */
export const MOBILE_MENU_ID = "app-nav-menu";

/** The account pill's screen-reader-only "My Account" label uses the shared recipe; re-exported for navbarPill.test.js. */
export { VISUALLY_HIDDEN_SX };

/**
 * Shared top navbar.
 *
 * Props:
 *   variant: "business" (default) | "recommender"
 *     "business"    — full nav: dashboard, BI, interventions, analytics, etc.
 *     "recommender" — minimal nav for the recommender dashboard
 *                     (logo + a single Dashboard link). Lets dual-role users
 *                     keep one consistent visual language across both views.
 *
 * SIGNED-IN SURFACES ONLY. Every public route (homepage, blog, landings, the
 * legal pages for a signed-out visitor) renders components/homepage/HomeNav.js
 * through AuthLayout instead, so this bar has no logged-out shape.
 *
 * LOOK (brand rollout, owner navy audit rounds 2-4): the homepage's floating
 * white pill, across the whole app - the shared `lf-pill-*` vocabulary of
 * components/navbar/pillNav.css (also HomeNav's) plus this bar's layout in
 * appNav.css. Brand lockup via components/brand/Logo.js, plain text items
 * with the navy active pill, the trial upgrade pill and the account menu at
 * the right; in the burger shape the round burger (44px, a 38px circle with
 * a 44px hit area on a phone) sits INSIDE the pill at its right end. The
 * in-flow spacer after the header keeps every page's first block out from
 * under the fixed bar.
 *
 * ROW OR BURGER is decided by FIT, not by a width (navbar/useRowFit.js): the
 * inline row shows only while it fits between the lockup and the
 * account pill with 16px of air each side; the width breakpoints in navTokens
 * are only the first-render guess, corrected before the first paint.
 */
/**
 * The sign-out failure notice. OWNER-APPROVED 2026-09-20 (id LG-01).
 *
 * ONE MESSAGE FOR BOTH FAILURE REASONS, and that is the owner's decision, not
 * a shortcut. The first draft split them and blamed the connection on the
 * `unreachable` branch ("check your connection and try again"); he rejected
 * that. It is guidance the user cannot act on - a 500 is not their network,
 * and even offline the useful instruction is the same one - so both reasons
 * say the same true thing: it did not finish, try again.
 *
 * Recorded in `scratchpad/trialconv/approved_copy_abcd.md` under LG-01,
 * because this is the one approved string in the release that lives in a
 * component callback rather than in a copy module.
 */
export const LOGOUT_FAILED_NOTICE = Object.freeze({
  he: "היציאה לא הושלמה. נסו שוב.",
  en: "Sign-out did not complete. Please try again.",
});

/**
 * The two outcomes that are NOT a successful sign-out.
 *
 * A SET of failures rather than a list of successes: `revoked`,
 * `already_ended` and `nothing_to_revoke` all mean the session is gone, and a
 * sixth outcome added later must default to saying nothing rather than to
 * telling a correctly signed-out user that something went wrong.
 */
const LOGOUT_FAILURE_REASONS = new Set(["unreachable", "refused"]);

/**
 * What to tell a user whose sign-out did not complete, or null when it did.
 *
 * `reason` IS STILL A PARAMETER although both failure branches return the same
 * text today. It is the seam: the owner chose one message over two, and if he
 * ever wants them split, the split happens here and nowhere else - the caller
 * already passes the reason and the destination already renders whatever comes
 * back.
 *
 * @param {string} reason one of the five `services/auth.js::logout` reasons.
 * @param {boolean} isHebrew
 * @returns {string|null} the notice, or null for every success outcome.
 */
export const logoutFailureNotice = (reason, isHebrew) =>
  LOGOUT_FAILURE_REASONS.has(reason)
    ? LOGOUT_FAILED_NOTICE[isHebrew ? "he" : "en"]
    : null;

function Navbar({
  isHebrew,
  toggleLanguage,
  variant = "business",
  // The upsell treatment for the chip. OMIT IT in production - the chip's
  // treatment is the owner's decision and lives in `UPSELL_SURFACE_VARIANT`.
  // Named apart from `variant` above, which is this navbar's own
  // business/recommender shape and has nothing to do with the upsell family.
  upsellVariant = variantForSurface("chip"),
}) {
  // The FIRST-RENDER GUESS for row vs burger (navTokens): the homepage pill's
  // width, or a little wider for an admin's longer row. The real switch is
  // the fit measurement below (`useRowFit`). Below the row floor (MUI's `md`)
  // it is always the burger. All three are read unconditionally (hooks).
  const belowMenuBreakpoint = useMediaQuery(`(max-width: ${NAV_MENU_BREAKPOINT_PX}px)`);
  const belowWideMenuBreakpoint = useMediaQuery(`(max-width: ${NAV_MENU_BREAKPOINT_WIDE_PX}px)`);
  const belowRowFloor = useMediaQuery(`(max-width: ${NAV_ROW_FLOOR_PX}px)`);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { openGuide, hasGuide } = useGuide();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = useState(null);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  // Suppressed inside the installed app — a user reading this menu from the
  // home-screen app must not be told to install it. Reactive so the entry
  // disappears if the document's display-mode flips mid-session.
  const isStandalone = useIsStandalonePwa();
  const showInstallEntry = !isStandalone;
  // The shared adaptive install action (utils/pwaInstall.js). Its own
  // availability check gates only WHAT the entry does, never whether it
  // renders - the instructions dialog stays a useful destination either way,
  // which is why `canOfferInstall` is deliberately not read here.
  const openInstallInstructions = useCallback(() => setInstallOpen(true), []);
  const { install } = useInstallAction({ onUnavailable: openInstallInstructions });

  // Translations — memoized, only rebuilds when language changes
  const text = useMemo(() => ({
    dashboard: isHebrew ? "לוח בקרה" : "Dashboard",
    // Recommender variant only (recommender earnings PR-4). Draft, pending
    // owner approval; the page's own title lives in earnings/earningsText.js.
    myEarnings: isHebrew ? "ההכנסות שלי" : "My earnings",
    analytics: isHebrew ? "ניתוח נתונים" : "Analytics",
    personalArea: isHebrew ? "אזור אישי" : "Personal Area",
    profile: isHebrew ? "פרופיל" : "Profile",
    settings: isHebrew ? "הגדרות" : "Settings",
    verifiedInterventions: isHebrew ? "התערבות בשיח ברשת" : "Online Interventions",
    myWebsite: isHebrew ? "האתר שלי" : "My Website",
    businessIntelligence: isHebrew ? "מודיעין עסקי" : "Business Intelligence",
    admin: isHebrew ? "ניהול" : "Admin",
    logout: isHebrew ? "התנתק" : "Logout",
    myAccount: isHebrew ? "החשבון שלי" : "My Account",
    accessibility: isHebrew ? "נגישות" : "Accessibility",
    installApp: isHebrew ? "התקנת האפליקציה" : "Install the app",
    guide: isHebrew ? "סיור היכרות" : "Quick Tour",
    upgrade: isHebrew ? "שדרג" : "Upgrade",
    switchToBusiness: isHebrew ? "מעבר לאזור עסקי" : "Switch to Business",
    switchToRecommender: isHebrew ? "מעבר לאזור ממליץ" : "Switch to Recommender",
    // The phone burger's names - HomeNav's own pair, so both bars say the same.
    menuOpen: isHebrew ? "פתח תפריט" : "Open menu",
    menuClose: isHebrew ? "סגור תפריט" : "Close menu",
    // The phone menu's admin group heading. Hebrew owner-approved verbatim
    // (navy audit round 4); the English label is a DRAFT pending approval.
    adminTools: isHebrew ? "כלי ניהול" : "Admin tools",
  }), [isHebrew]);

  const isRecommenderVariant = variant === "recommender";

  // ─── Early-upgrade offer on the pill ───────────────────────────────────
  // The hook is a per-user singleton read shared with the dashboard banner and
  // the checkout; it no-ops for anyone who is not a trialing non-admin, so it
  // is safe to call unconditionally here (hooks cannot be called in a branch).
  const { offer: upgradeOffer, loading: upgradeOfferLoading } = useUpgradeOffer();

  // Trial-user upgrade affordance — surfaces for the whole trial window
  // (including final urgent days). SubscriptionBanner on the Dashboard
  // handles the ≤3-day urgent state; this pill is the calm persistent nudge.
  // Hidden in the recommender variant: the recommender dashboard isn't part
  // of the billing surface.
  //
  // THE TRIAL STRIP REPLACES IT (card on file, owner ROUND 6): whenever the
  // dashboard's permanent trial strip applies to this user
  // (`resolveTrialOfferStrip` - the card-on-file feature live, a live
  // early-upgrade offer), the pill, the phone app-bar chip and the phone menu
  // row are all withheld, on every page. So is it for a user who already SAVED
  // a payment method (a live schedule - the plan is chosen, and in a failed
  // first charge's grace the dashboard asks for a new payment method instead):
  // "upgrade" would contradict both. Every other trial user (the feature off, a
  // friend-code offer, no offer) keeps the pill exactly as before.
  //
  // WHILE THE OFFER IS BEING READ the answer is unknown, so the pill is HELD
  // (review FFE-2): showing it and then withdrawing it once the read says
  // "the strip applies" made it pop in and out on every cold load. Held, it
  // appears once the read settles - the same moment it used to change from
  // the plain "upgrade" pill into the offer chip anyway. A failed read settles
  // with no offer, and the pill shows (fail open, as before). Only a trialing
  // user is ever read, so nobody else waits.
  const stripReplacesPill = useMemo(
    () =>
      (upgradeOfferLoading && !upgradeOffer) ||
      resolveTrialOfferStrip(upgradeOffer, user) !== null ||
      resolveCardOnFileView(upgradeOffer).scheduled,
    [upgradeOfferLoading, upgradeOffer, user]
  );
  const showUpgradePill =
    !isRecommenderVariant && !user?.is_admin && user?.status === "trial" && !stripReplacesPill;
  // The dashboard's last-day band carries its own "שדרג" button: while it is
  // on screen the BAR pill (desktop) and chip (phone bar) stand down, so the
  // screen has one upgrade button, not two. The phone menu's row stays - it
  // is never on screen together with the band's button.
  const lastDayBandShown = useLastDayBandOnScreen();
  const showBarUpgradePill = showUpgradePill && !lastDayBandShown;

  // The chip reads the ONE offer view-model (utils/friendCodeOffer), like every
  // other selling surface: a friend-code (referral) customer gets the approved
  // "P% הנחת חבר" chip and can never be shown CD-01; an early-upgrade / win-back
  // offer keeps CD-01 with the PROFESSIONAL plan's saving (the plan the offer
  // headline is quoted against everywhere else, so the number on the pill is the
  // number the checkout shows for that card). No offer, an ineligible one, a
  // failed fetch or a missing payload all fall through to the plain "שדרג"
  // label - fail-closed by construction.
  const offerView = useMemo(() => resolveOfferView(upgradeOffer, user), [upgradeOffer, user]);
  const { label: upgradeLabel, hasOffer: chipHasOffer } = useMemo(
    () => navbarChipView(offerView, { isHebrew, plainLabel: text.upgrade }),
    [offerView, isHebrew, text.upgrade]
  );

  // `shown` / `cta` are BOTH gated on an eligible offer, so the pair stays a
  // clean funnel for one surface. A plain-label pill click is deliberately not
  // measured here: it was never measured before this change, and emitting a
  // `cta` with no matching `shown` would put an un-normalisable ratio into a
  // table that is read in aggregate.
  //
  // `shown` fires once per OFFER KIND per mount (L-02, the checkout's
  // `checkoutShownKindsRef` twin). The navbar stays mounted across routes, so a
  // trialing user who saw the early-upgrade chip and then applies a friend code
  // at checkout now sees a REFERRAL chip - and that chip's later
  // `upgrade_offer_cta` (discount_kind "referral") needs a referral `shown` to
  // pair with. One per mount would leave that ratio un-pairable.
  const hasNavbarOffer = Boolean(showUpgradePill && chipHasOffer);
  const navbarShownKindsRef = useRef(new Set());
  useEffect(() => {
    if (!hasNavbarOffer || navbarShownKindsRef.current.has(offerView.kind)) return;
    navbarShownKindsRef.current.add(offerView.kind);
    try {
      // Numeric properties only, and each is omitted rather than sent
      // null/undefined when the server did not carry it.
      const percent = Number(upgradeOffer?.discount_percent);
      const days = Number(upgradeOffer?.days_left);
      analytics.trackEvent("upgrade_offer_shown", {
        surface: "navbar",
        ...(Number.isFinite(percent) ? { discount_percent: percent } : {}),
        ...(Number.isFinite(days) ? { days_left: days } : {}),
        // `discount_kind: "referral"` on a friend-code chip only, so its
        // impressions never pool with the early-upgrade ones.
        ...offerKindEventParams(offerView),
      });
    } catch (error) {
      /* instrumentation must never break the navbar */
    }
  }, [hasNavbarOffer, upgradeOffer, offerView]);

  // Dual-role accounts: a billing tier (trial/professional/enterprise) AND
  // an active Recommender profile. These users get a "Switch view" entry in
  // the menu so they can move between dashboards without re-logging in.
  // Server-side guards still enforce per-endpoint access; this is purely UX.
  const _TIER_TYPES = ["trial", "solo", "professional", "enterprise"];
  const isDualRole =
    !!user &&
    _TIER_TYPES.includes(user.user_type) &&
    !!user.is_recommender;

  // Every recommender-variant page (the dashboard AND /recommender/earnings)
  // is the recommender view, so the dual-role switch offers "to business".
  const isOnRecommenderView =
    isRecommenderVariant || location.pathname.startsWith("/recommender-dashboard");

  const handleUpgradeClick = useCallback(() => {
    if (hasNavbarOffer) {
      try {
        analytics.trackEvent("upgrade_offer_cta", {
          surface: "navbar",
          ...offerKindEventParams(offerView),
        });
      } catch (error) {
        /* never block the navigation on instrumentation */
      }
    }
    setAnchorEl(null);
    setMobileAnchorEl(null);
    navigate("/payment");
  }, [navigate, hasNavbarOffer, offerView]);

  const handleSwitchRole = useCallback(() => {
    setAnchorEl(null);
    setMobileAnchorEl(null);
    if (isOnRecommenderView) {
      setActiveRole("business");
      navigate("/dashboard");
    } else {
      setActiveRole("recommender");
      navigate("/recommender-dashboard");
    }
  }, [isOnRecommenderView, navigate]);

  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMobileMenu = useCallback((event) => {
    setMobileAnchorEl(event.currentTarget);
  }, []);

  const handleMobileClose = useCallback(() => {
    setMobileAnchorEl(null);
  }, []);

  const handleNavigation = useCallback((path) => {
    navigate(path);
    setAnchorEl(null);
    setMobileAnchorEl(null);
  }, [navigate]);

  // The phone menu's "ארכיון" row: to the dashboard with a one-shot "open the
  // archive" state the dashboard spends and clears. Already there, the entry
  // is REPLACED, so the row adds no history step.
  // Already on the dashboard, its URL is KEPT (a `?label=` included): the
  // dashboard opens the archive and drops the label itself, in ONE request -
  // dropping it here too made the label view's own refetch race the archive.
  const handleOpenArchive = useCallback(() => {
    const onDashboard = location.pathname === "/dashboard";
    navigate(
      onDashboard ? { pathname: "/dashboard", search: location.search || "" } : "/dashboard",
      openArchiveNavigation(onDashboard)
    );
    setAnchorEl(null);
    setMobileAnchorEl(null);
  }, [navigate, location.pathname, location.search]);

  const handleLogout = useCallback(async () => {
    setAnchorEl(null);
    setMobileAnchorEl(null);
    // `logout()` commits its state update synchronously (flushSync) and never
    // rejects; it RETURNS whether the server agreed. See
    // `services/auth.js::logout` for the five outcomes - the two 401/403 ones
    // count as success, because a session that is already gone is what the
    // user asked for.
    const result = await logout();
    if (result && result.ok === false) {
      // The sign-out did not complete on the server. The local teardown has
      // happened either way, so this tab is signed out - but it must not land
      // on the marketing home as though all was well. /login is the honest
      // destination: it is where signing in again happens, and it renders
      // `location.state.message`, which is the slot the notice travels in
      // (the `passwordSignOutNotice` mechanism, unchanged).
      const notice = logoutFailureNotice(result.reason, isHebrew);
      navigate('/login', {
        replace: true,
        // The spread, not a plain `state: { message: notice }`: a success
        // reason returns null, and handing /login a message of `null` would
        // mount its info Alert around nothing.
        ...(notice ? { state: { message: notice } } : {}),
      });
      return;
    }
    navigate('/');
  }, [logout, navigate, isHebrew]);

  const handleToggleLanguage = useCallback(() => {
    toggleLanguage();
    setMobileAnchorEl(null);
    setAnchorEl(null);
  }, [toggleLanguage]);

  const handleOpenAccessibility = useCallback(() => {
    setAccessibilityOpen(true);
    setMobileAnchorEl(null);
    setAnchorEl(null);
  }, []);

  const handleCloseAccessibility = useCallback(() => setAccessibilityOpen(false), []);

  // Permanent "install the app" entry, shared by the desktop dropdown and the
  // mobile drawer. Closes the menu first for the same reason handleOpenGuide
  // does: otherwise the dialog renders behind the MUI Menu backdrop.
  //
  // The adaptive behaviour itself (real installer where the browser offers one,
  // the shared InstallInstructionsDialog everywhere else) lives in
  // useInstallAction - see utils/pwaInstall.js for why the fallback branch is
  // load-bearing rather than a nicety.
  //
  // `install()` must be reached with NO prior await, or the browser no longer
  // counts this as a user gesture and refuses to show the prompt. The three
  // state setters above it are synchronous, so the gesture survives them.
  const handleInstallClick = useCallback(() => {
    setMobileAnchorEl(null);
    setAnchorEl(null);
    install();
  }, [install]);

  const handleCloseInstall = useCallback(() => setInstallOpen(false), []);

  // Open the feature guide from any menu, closing the menu first so the dialog
  // isn't rendered behind the MUI Menu backdrop.
  const handleOpenGuide = useCallback(() => {
    setMobileAnchorEl(null);
    setAnchorEl(null);
    openGuide();
  }, [openGuide]);

  // Alt + A keyboard shortcut — open/close accessibility settings from anywhere.
  // Skipped when focus is inside a text input so typing is never interrupted.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.altKey || e.key !== 'a') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      setAccessibilityOpen((prev) => !prev);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Logo click target. Recommender variant → recommender dashboard. Default →
  // /dashboard. The pathname-equality short-circuit avoids a no-op navigate
  // when the user is already on that page.
  const handleLogoClick = useCallback(() => {
    if (isRecommenderVariant) {
      if (location.pathname !== "/recommender-dashboard") {
        navigate("/recommender-dashboard");
      }
    } else if (location.pathname !== "/dashboard") {
      navigate("/dashboard");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [isRecommenderVariant, location.pathname, navigate]);

  // navItems memoized — rebuilds only when labels, user permissions, or path changes.
  // Icons store component type + default color; active color is applied at render time.
  // The recommender variant has its own minimal item list because that dashboard
  // is intentionally focused (no analytics, BI, settings, admin).
  const navItems = useMemo(() => {
    if (isRecommenderVariant) {
      return [
        {
          text: text.dashboard,
          Icon: LayoutGrid,
          path: "/recommender-dashboard",
          show: true,
        },
        {
          // The recommender's own earnings (recommender earnings PR-4).
          text: text.myEarnings,
          Icon: Wallet,
          path: "/recommender/earnings",
          show: true,
        },
      ];
    }
    return [
      {
        text: text.dashboard,
        Icon: LayoutGrid,
        path: "/dashboard",
        show: true,
      },
      {
        text: text.businessIntelligence,
        Icon: Aperture,
        path: "/business-intelligence",
        show: user?.has_active_bi_monitor || user?.is_admin,
      },
      {
        text: text.verifiedInterventions,
        Icon: Fingerprint,
        path: "/interventions/verified",
        show: user?.intervention_enabled || user?.is_admin,
      },
      {
        text: text.analytics,
        Icon: ChartNoAxesCombined,
        path: "/analytics",
        show: true,
      },
      {
        // Business-website builder — flagship feature. Eligibility is a
        // server-derived flag (admins included server-side), so the navbar
        // no longer hardcodes the tier list.
        text: text.myWebsite,
        Icon: PanelsTopLeft,
        path: "/website",
        show: Boolean(user?.website_eligible),
      },
      {
        text: text.personalArea,
        Icon: User,
        path: "/personal-area",
        show: true,
      },
      {
        text: text.settings,
        Icon: Settings,
        path: "/settings",
        show: true,
      },
      // Interventions + quota management are no longer navbar links: they
      // are the admin dashboard's Interventions tab (/admin?tab=14, sub-tab
      // ?sub=quotas), reached through "ניהול" below.
      {
        text: text.admin,
        Icon: ShieldEllipsis,
        path: "/admin",
        show: user?.is_admin,
      },
    ];
  }, [text, user?.intervention_enabled, user?.has_active_bi_monitor, user?.is_admin, user?.website_eligible, isRecommenderVariant]);


  // Stable callback — location.pathname is the only relevant dependency
  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  // The items the bar and the menus render, in their order.
  const visibleItems = useMemo(() => navItems.filter((item) => item.show), [navItems]);

  // ─── Row or burger: by FIT (navbar/useRowFit.js) ──────────────────────
  // The signed-in row's links and desktop actions stay in the DOM in the
  // burger shape too - invisible, inert and out of flow - wherever the
  // browser can measure them, so the decision is made from their real widths
  // (language, items, name, upgrade label) instead of a guessed breakpoint.
  const guessIsMobile =
    visibleItems.length >= NAV_WIDE_ROW_ITEMS ? belowWideMenuBreakpoint : belowMenuBreakpoint;
  const measureRow = canMeasureRow();
  const navInnerRef = useRef(null);
  const brandGroupRef = useRef(null);
  const linksRowRef = useRef(null);
  const rowActionsRef = useRef(null);
  const rowContentKey = [
    isHebrew ? "he" : "en",
    visibleItems.map((item) => item.path).join(","),
    isDualRole ? "dual" : "",
    showBarUpgradePill ? upgradeLabel : "",
    user?.full_name || "",
  ].join("|");
  const rowFits = useRowFit({
    enabled: measureRow,
    initialRow: !guessIsMobile,
    innerRef: navInnerRef,
    brandRef: brandGroupRef,
    linksRef: linksRowRef,
    actionsRef: rowActionsRef,
    contentKey: rowContentKey,
  });
  // Phone shape (burger inside the pill) vs the full row.
  const isMobile = belowRowFloor || (rowFits === null ? guessIsMobile : !rowFits);
  // The desktop row is in the DOM: shown, or kept as the invisible measure.
  const renderRow = !isMobile || measureRow;
  // The props that turn the row's parts into the invisible measuring copy.
  const measureOnlyProps = isMobile ? { "aria-hidden": "true", inert: true } : {};

  // A change of shape (a rotation, a resize, a longer row) removes the
  // control the open menu is anchored to - the burger or the account pill -
  // so that menu closes with it. The menus' `open` also requires the matching
  // shape, so not even the render before this effect anchors a menu to a
  // hidden control.
  useEffect(() => {
    if (isMobile) setAnchorEl(null);
    else setMobileAnchorEl(null);
  }, [isMobile]);

  // PHONE MENU GROUPING (owner, navy audit round 4): for an ADMIN the three
  // admin-tool links leave the everyday list and sit together under their own
  // heading. A non-admin customer's BI / interventions links are their own
  // product features, so they stay where they are and no heading is drawn -
  // which also means the heading only ever renders with links under it.
  const { everydayItems, adminToolItems } = useMemo(() => {
    if (isRecommenderVariant || !user?.is_admin) {
      return { everydayItems: visibleItems, adminToolItems: [] };
    }
    return {
      everydayItems: visibleItems.filter((item) => !ADMIN_TOOL_PATHS.includes(item.path)),
      adminToolItems: visibleItems.filter((item) => ADMIN_TOOL_PATHS.includes(item.path)),
    };
  }, [visibleItems, isRecommenderVariant, user?.is_admin]);

  // A bar item is a real link: it carries its href (open-in-new-tab and
  // middle-click work), and a plain click stays in the SPA. A modified click
  // is left to the browser.
  const onNavLinkClick = useCallback(
    (event, path) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      handleNavigation(path);
    },
    [handleNavigation]
  );

  // The logo's destination (see handleLogoClick for the click itself).
  const logoHref = isRecommenderVariant ? "/recommender-dashboard" : "/dashboard";
  const onLogoLinkClick = useCallback(
    (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      handleLogoClick();
    },
    [handleLogoClick]
  );

  // One menu row for a nav item - shared by the desktop account menu and the
  // phone menu, so the two stay in lockstep. The current page takes the bar's
  // own navy pill; BI and interventions keep only their feature colour on the
  // icon (SIGNATURE_ICON_COLORS).
  // An item may carry its own `onSelect` + `key` instead of a `path` (the
  // phone menu's archive row): it is never "the current page".
  const renderMenuNavItem = (item, { phone, describedBy }) => {
    const active = item.path ? isActive(item.path) : false;
    const signatureColor = item.path ? SIGNATURE_ICON_COLORS[item.path] : undefined;
    return (
      <MenuItem
        key={item.key || item.path}
        onClick={item.onSelect || (() => handleNavigation(item.path))}
        selected={active}
        aria-current={active ? "page" : undefined}
        aria-describedby={describedBy}
        sx={{
          mx: 1,
          my: 0.2,
          ...(phone ? { padding: "6px 16px", minHeight: "auto" } : {}),
          borderRadius: "10px",
          transition: "background-color 0.2s ease",
          backgroundColor: active ? NAV_ACTIVE_BG : "transparent",
          "&:hover": { backgroundColor: active ? NAV_ACTIVE_BG : colors.primary.alpha08 },
          "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
            backgroundColor: NAV_ACTIVE_BG,
          },
        }}
      >
        <ListItemIcon>
          <item.Icon
            size={phone ? 20 : 18}
            strokeWidth={1.5}
            color={signatureColor || (active ? NAV_INK : colors.text.label)}
          />
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          sx={{
            "& .MuiTypography-root": {
              fontSize: "14px",
              fontWeight: active ? 600 : 400,
              color: active ? NAV_INK : "inherit",
            },
          }}
        />
      </MenuItem>
    );
  };

  const switchLabel = isOnRecommenderView ? text.switchToBusiness : text.switchToRecommender;

  // ─── The two menus, as FLAT lists ─────────────────────────────────────
  // MUI's MenuList moves keyboard focus only across its DIRECT children (it
  // walks `nextElementSibling` and focuses whatever carries a tabindex). A
  // MenuItem wrapped in a Box is unreachable by the arrow keys - which is how
  // both menus used to be built (reviews R2 #1, R4 #5). So every row here is
  // a direct child: actionable rows are MenuItems, separators are Dividers,
  // and the name/email block and the admin heading are non-focusable
  // ListSubheaders, which MenuList skips. Each menu is an ARRAY (MUI refuses a
  // Fragment child) with its falsy entries filtered out.

  const menuDividerSx = { my: 0.5, borderColor: "rgba(224, 224, 224, 0.2)" };

  // A utilities row (install / tour / language / accessibility) - the phone
  // menu draws it quieter than the desktop one, as it always has.
  const renderUtilityRow = ({ key, onClick, Icon, label, phone }) => (
    <MenuItem
      key={key}
      onClick={onClick}
      sx={{
        mx: 1,
        my: phone ? 0.15 : 0.25,
        ...(phone ? { padding: "4px 16px", minHeight: "auto" } : {}),
        borderRadius: "10px",
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: phone ? "rgba(0, 0, 0, 0.04)" : colors.primary.alpha08 },
      }}
    >
      <ListItemIcon>
        {phone ? (
          <Icon size={18} color={colors.text.secondary} />
        ) : (
          <Icon size={18} strokeWidth={1.5} color={colors.text.label} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={label}
        sx={{
          "& .MuiTypography-root": phone
            ? { fontSize: "13px", color: "text.secondary" }
            : { fontSize: "14px" },
        }}
      />
    </MenuItem>
  );

  // The install / tour / language / accessibility rows, in ONE order for both
  // menus (review FR2 #2): install first (the surface that matters most for PWA
  // installs), then the tour, then language before accessibility - the order
  // the desktop account menu and the homepage sheet already used. Only the
  // row's look differs by `phone`.
  const utilityRows = (phone) => {
    const install =
      showInstallEntry &&
      renderUtilityRow({ key: "install", onClick: handleInstallClick, Icon: Smartphone, label: text.installApp, phone });
    const guide =
      hasGuide &&
      renderUtilityRow({ key: "guide", onClick: handleOpenGuide, Icon: BookOpen, label: text.guide, phone });
    const language = renderUtilityRow({
      key: "language",
      onClick: handleToggleLanguage,
      Icon: Globe,
      label: isHebrew ? "English" : "עברית",
      phone,
    });
    const accessibility = renderUtilityRow({
      key: "accessibility",
      onClick: handleOpenAccessibility,
      Icon: PersonStanding,
      label: text.accessibility,
      phone,
    });
    return [install, guide, language, accessibility];
  };

  // The signed-in user's name + email at the top of either menu. A heading,
  // not a row: MenuList skips it, so the arrows start on the first real item.
  const renderAccountHeader = (phone) => (
    <ListSubheader
      key="account-header"
      role="presentation"
      disableSticky
      sx={{
        px: 2.5,
        py: phone ? 1 : 2,
        mb: 0.5,
        lineHeight: "normal",
        color: "inherit",
        backgroundColor: "transparent",
        borderBottom: "1px solid rgba(224, 224, 224, 0.2)",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          mb: phone ? 0.3 : 0.5,
          overflowWrap: "anywhere",
          ...(phone ? { fontSize: "15px" } : {}),
        }}
      >
        {user?.full_name || "User"}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={phone ? { fontSize: "12px" } : undefined}>
        {user?.email}
      </Typography>
    </ListSubheader>
  );

  const renderSwitchRoleRow = (phone) => (
    <MenuItem
      key="switch-role"
      onClick={handleSwitchRole}
      sx={{
        mx: 1,
        my: phone ? 0.15 : 0.25,
        ...(phone ? { padding: "4px 16px", minHeight: "auto" } : {}),
        borderRadius: "10px",
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: colors.primary.alpha08 },
      }}
    >
      <ListItemIcon>
        <Repeat size={18} strokeWidth={1.5} color={colors.text.label} />
      </ListItemIcon>
      <ListItemText primary={switchLabel} sx={{ "& .MuiTypography-root": { fontSize: "14px" } }} />
    </MenuItem>
  );

  const renderLogoutRow = (phone) => (
    <MenuItem
      key="logout"
      onClick={handleLogout}
      sx={{
        mx: 1,
        my: phone ? 0.15 : 0.25,
        ...(phone ? { padding: "4px 16px", minHeight: "auto" } : {}),
        borderRadius: "10px",
        color: "#FF69B4",
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: "rgba(255, 182, 193, 0.08)" },
      }}
    >
      <ListItemIcon>
        <Logout sx={{ color: colors.accent.main, fontSize: 20 }} />
      </ListItemIcon>
      <ListItemText primary={text.logout} sx={{ "& .MuiTypography-root": { fontSize: "14px" } }} />
    </MenuItem>
  );

  // Only for an account that can use the archive at all (archiveAccess.js). An
  // admin in the "all users" view is moved to "my leads" by the dashboard for
  // it - unsaved, and put back when they leave the archive.
  const phoneArchiveItem =
    !isRecommenderVariant && accountMayUseArchive(user)
      ? { key: "archive", text: archiveText(isHebrew).archiveLink, Icon: Archive, onSelect: handleOpenArchive }
      : null;

  const phoneMenuChildren = [
    // Who is signed in - a heading, not a row.
    renderAccountHeader(true),

    // Upgrade affordance (trial users only). A row-shaped chip from the
    // upsell family; the skin and the bolded amount live in
    // `upsell/upsellStyles`, so this file owns no colour.
    showUpgradePill && (
      <UpgradeDrawerRow
        key="upgrade"
        variant={upsellVariant}
        label={upgradeLabel}
        hasOffer={hasNavbarOffer}
        onClick={handleUpgradeClick}
      />
    ),

    // The archive row sits directly under "הגדרות", above the utilities
    // (install first) - owner placement. It is the phone's one door into the
    // archive (the dashboard's xs filter panel keeps only the clear-feed
    // actions).
    ...everydayItems.flatMap((item) => {
      const row = renderMenuNavItem(item, { phone: true });
      return item.path === "/settings" && phoneArchiveItem
        ? [row, renderMenuNavItem(phoneArchiveItem, { phone: true })]
        : [row];
    }),

    // The admin tools under their own heading (admins only - see
    // `adminToolItems`). No wrapper (it would hide the rows from the arrow
    // keys): each row names the heading through aria-describedby instead.
    ...(adminToolItems.length > 0
      ? [
          <ListSubheader
            key="admin-heading"
            id={ADMIN_GROUP_HEADING_ID}
            role="presentation"
            disableSticky
            data-testid="navbar-admin-tools-heading"
            sx={{
              mt: 0.75,
              px: 2.5,
              pt: 1.5,
              pb: 0.5,
              borderTop: `1px solid ${NAV_HAIRLINE}`,
              backgroundColor: "transparent",
              fontSize: "11.5px",
              fontWeight: 500,
              letterSpacing: "0.06em",
              lineHeight: 1.4,
              color: NAV_SLATE,
            }}
          >
            {text.adminTools}
          </ListSubheader>,
          ...adminToolItems.map((item) =>
            renderMenuNavItem(item, { phone: true, describedBy: ADMIN_GROUP_HEADING_ID })
          ),
        ]
      : []),

    <Divider key="utilities-start" sx={menuDividerSx} />,
    ...utilityRows(true),

    <Divider key="account-end" sx={menuDividerSx} />,
    isDualRole && renderSwitchRoleRow(true),
    renderLogoutRow(true),
  ].filter(Boolean);

  // The desktop account menu repeats the bar's items, as it always has
  // (owner, navy audit round 4: desktop stays as-is).
  const accountMenuChildren = [
    renderAccountHeader(false),
    ...visibleItems.map((item) => renderMenuNavItem(item, { phone: false })),
    <Divider key="utilities-start" sx={menuDividerSx} />,
    ...utilityRows(false),
    <Divider key="account-end" sx={menuDividerSx} />,
    isDualRole && renderSwitchRoleRow(false),
    renderLogoutRow(false),
  ].filter(Boolean);

  return (
    <>
      {/* The homepage's floating pill, across the whole app (owner, navy audit
          round 3). Shared vocabulary: components/navbar/pillNav.css; this
          bar's own layout: appNav.css. */}
      <header
        className={`lf-pill-scope lf-app-nav${isMobile ? " lf-app-nav-phone" : ""}`}
        role="banner"
        data-testid="app-nav"
      >
        <nav
          id="navigation"
          aria-label="Main navigation"
          tabIndex={-1}
          className="lf-app-nav-inner lf-pill-surface"
          ref={navInnerRef}
        >
          {/* Left group: the brand lockup. Physically LTR in both languages. */}
          <div className="lf-app-nav-brand" ref={brandGroupRef}>
            {/* The brand lockup (icon + full name) via the shared Logo - never
                a typed wordmark. A real link to this view's home. The IMAGE
                names it (alt "LeadifyAI") and the link carries no aria-label
                of its own, so the link's accessible name is exactly
                "LeadifyAI", announced once (HomeNav does the same). */}
            <a
              href={logoHref}
              className="lf-pill-brand"
              onClick={onLogoLinkClick}
              data-testid="navbar-brand"
            >
              <Logo variant="lockup" height={NAV_LOCKUP_HEIGHT} />
            </a>
          </div>

          {/* Desktop nav items. Plain text, no icons, the
              current page in the navy pill (owner, navy audit rounds 2-3);
              BI / interventions / admin are more items in the same row. In
              the burger shape this row stays as the invisible, inert copy
              useRowFit measures (`lf-app-nav-measure`, appNav.css). */}
          {renderRow && (
            <div
              ref={linksRowRef}
              className={`lf-app-nav-links${isMobile ? " lf-app-nav-measure" : ""}`}
              dir={isHebrew ? "rtl" : "ltr"}
              data-testid="navbar-links"
              {...measureOnlyProps}
            >
              {visibleItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    className={`lf-pill-link${active ? " lf-pill-link-active" : ""}`}
                    aria-current={active ? "page" : undefined}
                    onClick={(e) => onNavLinkClick(e, item.path)}
                  >
                    {item.text}
                  </a>
                );
              })}

              {/* Role-switch — dual-role accounts only. A peer of the items
                  (inline-end of the row), but an ACTION, so a <button>. */}
              {isDualRole && (
                <button
                  type="button"
                  className="lf-pill-link"
                  onClick={handleSwitchRole}
                  aria-label={switchLabel}
                >
                  {switchLabel}
                </button>
              )}
            </div>
          )}

          {/* Right Side Actions - the desktop row's. Like the links, kept as
              the invisible measuring copy in the burger shape. */}
          {renderRow && (
            <div
              ref={rowActionsRef}
              className={`lf-app-nav-actions${isMobile ? " lf-app-nav-measure" : ""}`}
              {...measureOnlyProps}
            >
              {/* Upgrade affordance — Desktop (trial users only), beside the
                  account menu as the homepage's "הצטרף" sits beside "התחבר".
                  A CHIP, not a gradient button; `pillSx` owns the skin. */}
              {showBarUpgradePill && (
                <UpgradePill
                  variant={upsellVariant}
                  label={upgradeLabel}
                  hasOffer={hasNavbarOffer}
                  onClick={handleUpgradeClick}
                />
              )}

              {/* Account menu - Desktop: avatar + name, in the slot where the
                  homepage has "התחבר / הצטרף". */}
              <Box
                component="button"
                type="button"
                onClick={handleMenu}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchorEl)}
                data-testid="navbar-account"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.875,
                  height: 38,
                  boxSizing: "border-box",
                  px: 1,
                  m: 0,
                  font: "inherit",
                  borderRadius: "100px",
                  cursor: "pointer",
                  background: "rgba(255,255,255,0.72)",
                  border: "1px solid rgba(14, 22, 40, 0.16)",
                  boxShadow: "none",
                  transition: "background-color 0.22s ease, border-color 0.22s ease",
                  "&:hover": {
                    background: "rgba(255,255,255,0.92)",
                    borderColor: colors.primary.alpha35,
                  },
                  "&:focus-visible": {
                    outline: `2px solid #00A896`,
                    outlineOffset: "2px",
                  },
                }}
              >
                {/* Gradient initial circle */}
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, #00A896 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "11.5px",
                    fontWeight: 700,
                    fontFamily: '"DM Sans", sans-serif',
                    flexShrink: 0,
                    letterSpacing: "0.01em",
                  }}
                >
                  {user?.full_name?.charAt(0).toUpperCase() || "U"}
                </Box>

                {/* The control's name: "My Account" for screen readers, then
                    the visible name (WCAG 2.5.3 - the accessible name carries
                    the visible text, and the WHOLE name even when it is
                    ellipsed on screen). */}
                <Box component="span" sx={VISUALLY_HIDDEN_SX}>
                  {text.myAccount}
                </Box>

                {/* Name - visible on lg+; a long name ellipses, never pushes.
                    `dir="auto"`: the row is physically LTR, so without it a
                    Hebrew name would be cut at its BEGINNING. */}
                <Box
                  component="span"
                  dir="auto"
                  data-testid="navbar-account-name"
                  sx={{
                    display: { md: "none", lg: "block" },
                    fontSize: "13px",
                    fontWeight: 500,
                    fontFamily: "var(--lf-pill-font)",
                    color: NAV_INK,
                    maxWidth: NAV_ACCOUNT_NAME_MAX_PX,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  {user?.full_name || "User"}
                </Box>

                {/* Chevron */}
                <ChevronDown
                  size={13}
                  color="#5E7378"
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    transition: "transform 0.2s ease",
                    transform: Boolean(anchorEl) ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>
            </div>
          )}

          {/* Right Side Actions - the burger shape's. */}
          {isMobile && (
            <div className="lf-app-nav-actions lf-app-nav-phone-actions">
              {/* Upgrade chip - MOBILE APP BAR (trial users with a live offer).
                  It used to be `md`-and-up only, so a phone user met it only
                  after opening the menu; the menu row STAYS (the two are not
                  alternatives), this is additive beside the hamburger.
                  Compact by necessity: the chip shows the LEAD VERB ("שדרג" /
                  "Upgrade") - a slice of the approved string - and carries the
                  whole approved sentence as its accessible name. Not the amount:
                  the owner's call on 2026-09-20 was that a bare number in the app
                  bar reads as a price rather than a saving. See UpgradePill.
                  `hasNavbarOffer` gates it, so with no offer - and for anyone
                  not trialing - the bar carries no chip. */}
              {hasNavbarOffer && !lastDayBandShown && (
                // The wrapper lets the chip's 44px hit area bleed past the
                // thinner phone bar instead of setting its height (appNav.css).
                <span className="lf-app-nav-chip">
                  <UpgradePill
                    variant={upsellVariant}
                    label={upgradeLabel}
                    hasOffer
                    compact
                    // The friend-code chip is already the short form: whole.
                    compactWhole={offerView.kind === OFFER_KIND.REFERRAL}
                    onClick={handleUpgradeClick}
                  />
                </span>
              )}

              {/* The phone menu button: INSIDE the pill at its right end, the
                  homepage bar's shared round burger - 44px, a 38px circle
                  with a 44px hit area at <= 600px (pillNav.css; owner, navy
                  audit round 3 - it used to stick out past the bar's edge). */}
              <button
                type="button"
                className="lf-pill-burger"
                aria-label={mobileAnchorEl ? text.menuClose : text.menuOpen}
                aria-haspopup="menu"
                aria-expanded={Boolean(mobileAnchorEl)}
                aria-controls={mobileAnchorEl ? MOBILE_MENU_ID : undefined}
                onClick={handleMobileMenu}
                data-testid="navbar-burger"
              >
                {/* Lucide's menu / close glyph (owner, 2026-10-14); the
                    button carries the name and the state. */}
                {mobileAnchorEl ? (
                  <CloseIcon size={20} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
                ) : (
                  <MenuIcon
                    className="lf-pill-burger-lines"
                    size={20}
                    strokeWidth={2}
                    absoluteStrokeWidth
                    aria-hidden="true"
                  />
                )}
              </button>
            </div>
          )}
        </nav>
      </header>

      {/* User Dropdown Menu (Desktop) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !isMobile}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            minWidth: 240,
            maxWidth: 320,
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            // The menu's own reading direction follows the page language.
            direction: isHebrew ? "rtl" : "ltr",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {accountMenuChildren}
      </Menu>

      {/* Mobile Menu */}
      <Menu
        id={MOBILE_MENU_ID}
        anchorEl={mobileAnchorEl}
        open={Boolean(mobileAnchorEl) && isMobile}
        onClose={handleMobileClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            minWidth: 220,
            maxWidth: 280,
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            direction: isHebrew ? "rtl" : "ltr",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {phoneMenuChildren}
      </Menu>

      {/* The spacer: the bar is fixed, so every page starts under it. Its
          height is the pill's top offset + its height + the same air below
          (56px wide bar, 62px burger bar, 54px on a phone - appNav.css). */}
      <div
        className={`lf-pill-spacer lf-app-nav-spacer${isMobile ? " lf-pill-spacer-phone" : ""}`}
        aria-hidden="true"
        data-testid="app-nav-spacer"
      />

      {/* Accessibility Settings Dialog */}
      <SimpleAccessibilitySettings
        open={accessibilityOpen}
        onClose={handleCloseAccessibility}
      />

      {/* "How do I install the app?" — shared dialog, opened from the user
          menu (desktop) and the mobile drawer. */}
      <InstallInstructionsDialog open={installOpen} onClose={handleCloseInstall} />
    </>
  );
}

export default Navbar;
